import SubscriptionChangePreviewResponse from "interfaces/response/SubscriptionChangePreviewResponse";
import { connect } from "react-redux";
import { RootState } from "store/store";
import SubscriptionPlanInformation from "./SubscriptionPlanInformation";
import { getCurrencySymbol } from "shared/utility/CommonUtility";
import { BillingInterval } from "shared/enums/BillingIntervalEnum";
import moment from "moment";
import { useState } from "react";
import SubscriptionConfirmationActionFooter from "./SubscriptionConfirmationActionFooter";
import { getFormattedNumber } from "shared/utility/NumberUtility";

interface Props {
  previewData: SubscriptionChangePreviewResponse | null;
  onCancel?: () => void;
  onApply?: (previewData: SubscriptionChangePreviewResponse | null) => void;
}

export function SubscriptionConfirmation(props: Props) {
  const { previewData, onCancel, onApply } = props;
  const [currencySymbol] = useState(getCurrencySymbol());

  const getTitle = () => {
    let title = "";
    if (previewData?.isUpgradeRequest || previewData?.isNewOnlineSubscription) {
      title = "Upgrade subscription plan";
    } else if (
      !previewData?.isUpgradeRequest &&
      !previewData?.isNewOnlineSubscription
    ) {
      title = "Downgrade subscription plan";
    }
    return title;
  };

  const getFormattedPrice = () => {
    if (!previewData?.price) {
      return "Free";
    }

    const billingInterval =
      previewData?.billingInterval === BillingInterval.Monthly
        ? "month"
        : "year";
    return `${getCurrencySymbol()}${getFormattedNumber(previewData?.price ?? 0)} / ${billingInterval}`;
  };

  const getFormattedPeriodStartDate = () => {
    return moment(previewData?.periodStart).format("DD MMMM YYYY");
  };

  const getDescription = () => {
    let description: any = {};

    if (previewData?.isUpgradeRequest) {
      description.titleComponent = (
        <div className="text-sm font-bold">
          First payment{" "}
          <span className="text-primaryRed">
            {currencySymbol}
            {getFormattedNumber(previewData.amountPayable)}
          </span>
          , then {currencySymbol}
          {getFormattedNumber(previewData.price)}{" "}
        </div>
      );
      description.detailComponent = (
        <div className="text-sm">
          <p>
            You will receive a{" "}
            <span className="italic">
              {currencySymbol}
              {previewData.unusedAmount}
            </span>{" "}
            discount on your first month to compensate for previous subscription
            payments.
          </p>
          <p className="mt-1 italic">Your card will be charged immediately.</p>
        </div>
      );
    } else if (previewData?.isNewOnlineSubscription) {
      description.title = "Confirm your new subscription plan";
      description.detail =
        "You will be taken to our payment partner to complete purchase";
    } else if (
      !previewData?.isNewOnlineSubscription &&
      !previewData?.isUpgradeRequest
    ) {
      description.title = "Current plan runs until next billing cycle";
      description.detailComponent = (
        <>
          <p>Your new plan starts at the end of the current billing cycle:</p>
          <p className="italic">{getFormattedPeriodStartDate()}</p>
        </>
      );
    }

    return description;
  };

  const ActionFooter = () => {
    return (
      <SubscriptionConfirmationActionFooter
        onCancel={onCancel}
        onApply={() => onApply?.(previewData)}
        applyButtonCaption={
          previewData?.isNewOnlineSubscription ? "TO PAYMENT" : "CONFIRM"
        }
      />
    );
  };

  return (
    <div>
      {previewData && (
        <>
          <SubscriptionPlanInformation
            title={getTitle()}
            planDetail={{
              actions: previewData.numberOfActions!,
              price: getFormattedPrice(),
              starts: !previewData.periodStart
                ? "immediately"
                : getFormattedPeriodStartDate(),
            }}
            description={getDescription()}
          />
          <>
            <ActionFooter />
          </>
        </>
      )}
    </div>
  );
}

const mapStateToProps = (state: RootState) => {
  return {
    previewData: state.subscriptionData.subscriptionChangePreview,
  };
};

export default connect(mapStateToProps)(SubscriptionConfirmation);
